import React, { useState } from 'react';
import { WalletApi, WalletHelpers } from '../api';
import { Icons } from '../../Components';
import type { AccountProps, FocusPositionProps, MyPlayerProps } from '../../types';
import moment from 'moment-mini';
import { Button, Text, TextInput, View } from '../../Components/Themed';
import { useColors } from '../../constants/useColors';
import { FlatList } from 'react-native';

type ManualCardAddProps = {
    player:MyPlayerProps,
    onFocusPosition?:(pos:FocusPositionProps) => void,
    onAddAccount:(acct:AccountProps) => void,
    onCancel:() => void
}
const card_sections = ['header','card_number','first_name','last_name','expire_date']
const ManualCardAdd = ({ player, onAddAccount, onCancel, onFocusPosition }:ManualCardAddProps) => {
    const Colors = useColors();
    const [ loading, setLoading ] = useState(false);
    const [ error, setError ] = useState<string>();
    const [ card_account, setCardAccount ] = useState({
        account: WalletHelpers.getEmptyCardAccount(),
        card_detail: { ...WalletHelpers.getEmptyCardDetails(), first_name: player.first_name, last_name: player.last_name }
    })

    const { account, card_detail } = card_account;
    const { valid, card_number, first_name, last_name, expiration_date } = WalletHelpers.isCardValid(card_detail);

    const handleExpiry = (text:string) => {
        setCardAccount({ ...card_account, card_detail: { ...card_detail, expiration_date: text } })
    }

    const handleCardNumber = (text:string) => {
        let unformated_number = WalletHelpers.unformatCardNumber(text)
        setCardAccount({ ...card_account, card_detail: { ...card_detail, card_number: unformated_number, card_brand: WalletHelpers.getCardBrandFromNumber(text) } })
    }

    const handleSaveCard = async() => {
        if(!valid || loading){ return }
        setLoading(true);
        try {
            const resp = await WalletApi.addCardAccount(account, { ...card_detail, expiration_date: moment(card_detail.expiration_date, 'MM/YYYY') })
            //setCardAccount(resp)
            onAddAccount({ ...resp.account, card_detail: resp.card_detail })
            setError(undefined)
        } catch (e) {
            setError('Invalid card.  Please try again')
        }
        setLoading(false)
    }

    const renderCardIcon = (brand?:string) => {
        switch(brand){
            case 'AMEX':
                return <Icons.AmexCardIcon size={30} />
            case 'VISA':
                return <Icons.VisaCardIcon size={30} />
            case 'MASTERCARD':
                return <Icons.MasterCardIcon size={30} />
            case 'DISCOVER':
                return <Icons.DiscoverCardIcon size={30} />
            default:
                return <Icons.CreditCardIcon size={22} color={Colors.text.h1}/>
        }
    }

    const renderSections = (data:{item:string, index:number}) => {
        switch(data.item){
            case 'header':
                return (
                    <View type='header' style={{ padding:20 }}>
                        <Text theme='h1'>NEW CARD ACCOUNT</Text>
                        <Text style={{ marginTop:5 }} theme='description'>Complete the form below to add a new card account. Once successfully added, you can transact immediately.</Text>
                    </View>
                )
            case 'card_number':
                return (
                    <View style={{padding:10}}>
                        <View nativeID='card_number' style={{ padding:5 }}>
                            <View style={{ flexDirection:'row', alignItems:'center', padding:10 }}>
                                <Text style={{flex:1}} size={14} theme='h2'>Card Number</Text>
                                <View style={{ flexDirection:'row', alignItems:'center' }}>
                                    <View>
                                        <Icons.CreditCardIcon size={24} color={Colors.text.h1}/>
                                    </View>
                                    <View style={card_detail.card_brand == 'VISA' ? {} : {}}>
                                        <Icons.VisaCardIcon size={30} />
                                    </View>
                                    <View style={card_detail.card_brand == 'MASTERCARD' ? {} : {}}>
                                        <Icons.MasterCardIcon size={30} />
                                    </View>
                                    <View style={card_detail.card_brand == 'AMEX' ? {} : {}}>
                                        <Icons.AmexCardIcon size={30} />
                                    </View>
                                    <View style={card_detail.card_brand == 'DISCOVER' ? {} : {}}>
                                        <Icons.DiscoverCardIcon size={30} />
                                    </View>
                                    
                                </View>
                            </View>
                            <View type='input' style={{ flexDirection:'row' }}>
                                <View style={{ justifyContent:'center', alignItems:'center', paddingLeft:10, paddingRight:10 }}>
                                    {renderCardIcon(card_detail.card_brand)}
                                </View>
                                <TextInput 
                                    style={{ borderWidth:0, padding:15, flex:1 }}
                                    placeholder='1234 5678 9876 5432'
                                    onFocusPosition={onFocusPosition}
                                    keyboardType='decimal-pad'
                                    value={WalletHelpers.formatCardNumber(card_detail.card_number)}
                                    onChangeText={(e) => handleCardNumber(e)}
                                />
                                </View>
                        </View>
                        {card_detail.card_number && !card_number ?
                            <Text size={14} color={Colors.text.warning} weight='semibold'>Card number is not valid</Text>
                        :<></>}
                    </View>
                    
                )
            case 'first_name':
                return (
                    <View style={{padding:10}}>
                        <View nativeID='first_name' style={{ padding:5 }}>
                            <Text style={{ padding:10 }} size={14} theme='h2'>First Name</Text>
                                <TextInput 
                                    placeholder='Jonathan'
                                    onFocusPosition={onFocusPosition}
                                    value={card_detail.first_name}
                                    onChangeText={(e) => setCardAccount({ ...card_account, card_detail: { ...card_detail, first_name: e } })}
                                />
                        </View>
                        {!first_name ?
                            <Text size={14} color={Colors.text.warning} weight='semibold'>First name is not valid</Text>
                        :<></>}
                    </View>
                )
            case 'last_name':
                return (
                    <View style={{padding:10}}>
                        <View nativeID='last_name' style={{ padding:5 }}>
                            <Text size={14} theme='h2' style={{ padding:10 }}>Last Name</Text>
                            <TextInput 
                                onFocusPosition={onFocusPosition}
                                placeholder='Smith'
                                value={card_detail.last_name}
                                onChangeText={(e) => setCardAccount({ ...card_account, card_detail: { ...card_detail, last_name: e } })}
                            />
                        </View>
                        {!last_name ?
                            <Text size={14} color={Colors.text.warning} weight='semibold'>Last name is not valid</Text>
                        :<></>}
                    </View>
                )
            case 'expire_date':
                return (
                    <View style={{padding:10, paddingBottom:300}}>
                        <View nativeID='expiration_date' style={{ padding:5 }}>
                            <Text size={14} theme='h2' style={{ padding:10 }}>Expiration Date (MM/YYYY)</Text>
                            <TextInput
                                onFocusPosition={onFocusPosition}
                                value={card_detail.expiration_date}
                                placeholder={'MM/YYYY'}
                                onChangeText={(e) => handleExpiry(e)}
                            />
                        </View>
                        {card_detail.expiration_date && !expiration_date ?
                            <Text size={14} color={Colors.text.warning} weight='semibold'>Expiration date is not valid</Text>
                        :<></>}
                        {error ?
                        <View style={{ padding:10 }}>
                            <Text size={16} color={Colors.text.warning} weight='bold' textAlign='center'>{error}</Text>
                        </View>
                        :<></>}
                    </View>
                )
            default: return <></>
        }
    }

    return (
        <View style={{ flex:1 }}>
            <View style={{ flex:1 }}>
                <FlatList
                    data={card_sections}
                    key={'card_sections'}
                    keyExtractor={(item) => item}
                    renderItem={renderSections}
                />
            </View>
            <View style={{ flexDirection:'row', padding:20 }}>
                <Button
                    title='CANCEL'
                    style={{ flex:1, marginRight:5 }}
                    type='error'
                    onPress={() => onCancel()}
                />
                <Button
                    title='ADD ACCOUNT'
                    style={{ flex:3, opacity:valid&&!loading?1:0.5 }}
                    disabled={!valid||loading}
                    loading={loading}
                    type='success'
                    onPress={() => handleSaveCard()}
                />
            </View>
        </View>
    )
}



export default ManualCardAdd